import { Link } from "react-router-dom";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Layout from "./components/Layout";
import ScrollToTop from "./components/ScrollToTop";
import Analytics from "./components/Analytics";
import SEO from "./components/SEO";
import Home from "./pages/Home";
import About from "./pages/About";
import BookDemo from "./pages/BookDemo";
import ProductPage from "./pages/ProductPage";
import District from "./pages/District";
import FAQPage from "./pages/FAQPage";
import AlignmentCheck from "./pages/AlignmentCheck";
import Insights from "./pages/Insights";
import InsightLeadershipData from "./pages/InsightLeadershipData";
import InsightConsistencyTrust from "./pages/InsightConsistencyTrust";
import InsightActivityProgress from "./pages/InsightActivityProgress";
import InsightVisibilityImprovement from "./pages/InsightVisibilityImprovement";
import LeadershipPreparation from "./pages/LeadershipPreparation";
import { SOLUTIONS } from "./lib/site";
import "./App.css";

function NotFound() {
  return (
    <>
      <SEO title="Page not found | Aligned Education Systems" description="The page you're looking for doesn't exist." />
      <section className="min-h-[72vh] bg-[#FDFBF7] flex items-center" data-testid="notfound-section">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-20 w-full">
          <div className="text-xs uppercase tracking-[0.2em] text-[#52796F]">Error 404</div>
          <h1 className="mt-6 font-serif-display text-4xl sm:text-5xl lg:text-6xl text-[#0A192F] leading-[1.05]">
            We couldn&apos;t find that page.
          </h1>
          <p className="mt-6 text-lg text-[#4A5568] max-w-xl leading-relaxed">
            The page may have been moved or no longer exists. Here are a few places to go instead.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <Link to="/" className="inline-flex justify-center items-center bg-[#0A192F] text-[#FDFBF7] px-8 py-3.5 text-sm tracking-wide hover:bg-[#112240] transition-colors border border-[#0A192F]" data-testid="notfound-home-btn">
              Return Home
            </Link>
            <Link to="/book-a-demo" className="inline-flex justify-center items-center bg-transparent text-[#0A192F] px-8 py-3.5 text-sm tracking-wide hover:bg-[#F4F1EB] transition-colors border border-[#0A192F]" data-testid="notfound-demo-btn">
              Request a Demo
            </Link>
          </div>
          <div className="mt-16 border-t border-[#0A192F]/10 pt-10">
            <div className="text-xs uppercase tracking-widest text-[#52796F]">Explore Solutions</div>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              {SOLUTIONS.map((s) => (
                <Link key={s.slug} to={s.path} className="bg-[#F4F1EB] border border-[#0A192F]/10 p-5 hover:border-[#0A192F]/40 transition-colors" data-testid={`notfound-solution-${s.slug}`}>
                  <div className="font-serif-display text-lg text-[#0A192F]">{s.name}</div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <Analytics />
      <Layout>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/alignment-check" element={<AlignmentCheck />} />
          <Route path="/leadership-preparation" element={<LeadershipPreparation />} />
          <Route path="/insights" element={<Insights />} />
          <Route path="/insights/leadership-doesnt-have-a-data-problem" element={<InsightLeadershipData />} />
          <Route path="/insights/visibility-precedes-improvement" element={<InsightVisibilityImprovement />} />
          <Route path="/insights/consistency-creates-trust" element={<InsightConsistencyTrust />} />
          <Route path="/insights/activity-isnt-progress" element={<InsightActivityProgress />} />
          <Route path="/request-a-demo" element={<BookDemo />} />
          <Route path="/book-a-demo" element={<BookDemo />} />
          <Route path="/contact" element={<BookDemo />} />
          <Route path="/district-systems-overview" element={<District />} />
          <Route path="/solutions/attendance-intervention-system" element={<ProductPage slug="attendance-intervention-system" />} />
          {/* Backwards-compat: old attendance slugs → new slug */}
          <Route path="/solutions/attendance-recovery-system" element={<Navigate to="/solutions/attendance-intervention-system" replace />} />
          <Route path="/solutions/attendance-action-system" element={<Navigate to="/solutions/attendance-intervention-system" replace />} />
          <Route path="/solutions/behavior-consistency-system" element={<ProductPage slug="behavior-consistency-system" />} />
          {/* Backwards-compat: old slugs → new slug */}
          <Route path="/solutions/behavior-action-system" element={<Navigate to="/solutions/behavior-consistency-system" replace />} />
          <Route path="/solutions/behavior-execution-system" element={<Navigate to="/solutions/behavior-consistency-system" replace />} />
          <Route path="/solutions/enrollment-follow-through-system" element={<ProductPage slug="enrollment-follow-through-system" />} />
          {/* Backwards-compat: old enrollment slugs → new slug */}
          <Route path="/solutions/enrollment-growth-engine" element={<Navigate to="/solutions/enrollment-follow-through-system" replace />} />
          <Route path="/solutions/enrollment-growth-system" element={<Navigate to="/solutions/enrollment-follow-through-system" replace />} />
          {/* Retired demo page — route removed. Redirect stale links home. */}
          <Route path="/demo" element={<Navigate to="/" replace />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Layout>
    </BrowserRouter>
  );
}

export default App;
